import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ChevronRight, ClipboardList, MapPinOff, Pencil, Plus } from 'lucide-react';

import { MobileHeader } from '@/components/layout/MobileHeader';
import { MobileButton } from '@/components/ui/MobileButton';
import { MobileBadge } from '@/components/ui/MobileBadge';
import { MobileEmptyState } from '@/components/ui/MobileEmptyState';
import { MobileSkeleton } from '@/components/ui/MobileSkeleton';
import { useAuthStore } from '@/app/store/authStore';
import { listMySalesOrders } from '@/app/services/salesOrderService';
import { formatInr } from '@/app/lib/pricing';
import type { SalesOrder } from '@/app/types/salesOrder';

/**
 * Maps backend document statuses to what a manager actually needs to know:
 * has the office picked this up yet?
 */
function statusBadge(status: string) {
    switch (status) {
        case 'draft':
            return { label: 'Pending review', variant: 'warning' as const };
        case 'converted':
        case 'invoiced':
        case 'completed':
            return { label: 'Invoiced', variant: 'success' as const };
        case 'cancelled':
            return { label: 'Cancelled', variant: 'destructive' as const };
        case 'confirmed':
            return { label: 'Confirmed', variant: 'default' as const };
        default:
            return { label: status, variant: 'secondary' as const };
    }
}

function formatDate(iso: string): string {
    try {
        return format(new Date(`${iso}T00:00:00`), 'dd MMM');
    } catch {
        return iso;
    }
}

export default function MySalesOrdersPage() {
    const navigate = useNavigate();
    const user = useAuthStore((state) => state.user);

    const userSiteIds = useMemo(() => user?.sites?.map((s) => s.id) ?? [], [user]);
    const userId = user?.id ? Number(user.id) : null;
    const hasSites = userSiteIds.length > 0;

    const { data: orders, isLoading } = useQuery({
        queryKey: ['my-sales-orders', userId, userSiteIds],
        // GET /sales-orders/ resolves one site per request and 400s without one,
        // so don't call it at all when the account has no site association.
        enabled: !!userId && hasSites,
        staleTime: 30_000,
        queryFn: () => listMySalesOrders(userId!, userSiteIds),
    });

    return (
        <div className="flex flex-col min-h-full">
            <MobileHeader
                title="My Orders"
                rightAction={
                    <button
                        type="button"
                        aria-label="New order"
                        onClick={() => navigate('/sales-orders/new')}
                        className="flex items-center justify-center w-10 h-10 rounded-full text-primary active:bg-accent"
                    >
                        <Plus className="h-6 w-6" />
                    </button>
                }
            />

            <div className="flex-1 mobile-scroll-area pb-tab-bar">
                {!hasSites ? (
                    <MobileEmptyState
                        icon={<MapPinOff className="h-12 w-12" />}
                        title="No site assigned"
                        description="Your account isn't linked to a branch yet, so orders can't be listed here. Ask admin to assign you one. You can still create orders."
                        action={
                            <MobileButton onClick={() => navigate('/sales-orders/new')}>
                                New order
                            </MobileButton>
                        }
                    />
                ) : isLoading ? (
                    <OrdersSkeleton />
                ) : !orders || orders.length === 0 ? (
                    <MobileEmptyState
                        icon={<ClipboardList className="h-12 w-12" />}
                        title="No orders yet"
                        description="Orders you create will show up here with their status"
                        action={
                            <MobileButton onClick={() => navigate('/sales-orders/new')}>
                                New order
                            </MobileButton>
                        }
                    />
                ) : (
                    <div className="p-4 space-y-3">
                        {orders.map((order: SalesOrder) => {
                            const badge = statusBadge(order.status);
                            // Only orders the office hasn't picked up yet can still be
                            // corrected; the rest open read-only.
                            const editable = order.status === 'draft';
                            return (
                                <button
                                    key={order.id}
                                    type="button"
                                    onClick={() => navigate(`/sales-orders/${order.id}`)}
                                    className="mobile-card w-full text-left active:bg-accent transition-colors"
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <p className="font-semibold text-foreground tabular-nums">
                                            {order.document_number}
                                        </p>
                                        <div className="flex items-center gap-1.5 flex-shrink-0">
                                            <MobileBadge variant={badge.variant}>
                                                {badge.label}
                                            </MobileBadge>
                                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                        </div>
                                    </div>

                                    <p className="text-sm text-muted-foreground mt-1 truncate">
                                        {order.party?.name ?? 'Unknown customer'}
                                        {' · '}
                                        {formatDate(order.transaction_date)}
                                    </p>

                                    <div className="flex items-baseline justify-between mt-2">
                                        <span className="text-xs text-muted-foreground">
                                            {order.items?.length ?? 0}{' '}
                                            {(order.items?.length ?? 0) === 1 ? 'item' : 'items'}
                                            {order.site?.name ? ` · ${order.site.name}` : ''}
                                        </span>
                                        <span className="font-semibold text-foreground tabular-nums">
                                            {formatInr(Number(order.total_amount ?? 0))}
                                        </span>
                                    </div>

                                    {editable && (
                                        <span className="inline-flex items-center gap-1 text-xs font-medium text-primary mt-2">
                                            <Pencil className="h-3 w-3" />
                                            Tap to edit items
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

function OrdersSkeleton() {
    return (
        <div className="p-4 space-y-3">
            {[1, 2, 3, 4].map((i) => (
                <div key={i} className="mobile-card space-y-2">
                    <MobileSkeleton className="h-4 w-40" />
                    <MobileSkeleton className="h-3 w-32" />
                    <MobileSkeleton className="h-4 w-24" />
                </div>
            ))}
        </div>
    );
}
